import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ReplayFile } from '../../services/replay/types';
import { ReplayEngine } from '../../services/replay/ReplayEngine';
import { fetchGet } from '@par/shared/api/common';

interface Props {
  gameId: string;
  onReady: (engine: ReplayEngine, file: ReplayFile) => void;
}

type Status = 'loading' | 'error' | 'unsupported_version' | 'ready';

export const ReplayLoader: React.FC<Props> = ({ gameId, onReady }) => {
  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const { url } = await fetchGet(`/games/${gameId}/replay`);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`S3 fetch failed: ${res.status}`);

        // S3 is configured with Content-Encoding: gzip, so fetch decompresses automatically.
        const text = await res.text();

        if (cancelled) return;
        const file: ReplayFile = JSON.parse(text);

        if (file.version !== 1) {
          setStatus('unsupported_version');
          return;
        }
        const engine = new ReplayEngine(file);
        setStatus('ready');
        onReady(engine, file);
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message ?? 'Unknown error');
          setStatus('error');
        }
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [gameId]);

  if (status === 'loading') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2}>
        <CircularProgress />
        <Typography>Loading replay…</Typography>
      </Box>
    );
  }
  if (status === 'unsupported_version') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2}>
        <Typography variant="h6">This replay was recorded with an older format and can no longer be played back.</Typography>
      </Box>
    );
  }
  if (status === 'error') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2}>
        <Typography color="error">Failed to load replay: {errorMsg}</Typography>
        <Button variant="outlined" onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }
  return null;
};
